// src/protectedRoute.js
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { hasRouteAccess, getUserPermissions } from './permissionHelper'
import { CAlert, CContainer } from '@coreui/react-pro'

const ProtectedRoute = ({ children, requiredRoute, redirectTo, fallback, showAccessDenied }) => {
  const userPermissions = getUserPermissions()
  const location = useLocation()
  const hasAccess = hasRouteAccess(userPermissions, requiredRoute)

  if (!hasAccess) {
    // Show access denied message if enabled
    if (showAccessDenied) {
      return (
        <CContainer className="mt-5">
          <CAlert color="danger">
            <h4 className="alert-heading">Access Denied</h4>
            <p>
              You do not have permission to access this page. Please contact your administrator if
              you believe this is an error.
            </p>
            <hr />
            <p className="mb-0">
              Required route: <code>{requiredRoute}</code>
            </p>
          </CAlert>
        </CContainer>
      )
    }

    // Show custom fallback if provided
    if (fallback) {
      return fallback
    }

    // Redirect to specified page
    return <Navigate to={redirectTo} replace state={{ from: location }} />
  }

  return children
}

ProtectedRoute.propTypes = {
  /** Child components to render if access is granted */
  children: PropTypes.node.isRequired,

  /** Route path to check permission for (e.g., '/adduser') */
  requiredRoute: PropTypes.string.isRequired,

  /** Path to redirect to if access is denied */
  redirectTo: PropTypes.string,

  /** Custom component to show instead of redirecting */
  fallback: PropTypes.node,

  /** Show access denied message instead of redirecting */
  showAccessDenied: PropTypes.bool,
}

ProtectedRoute.defaultProps = {
  redirectTo: '/dashboard',
  fallback: null,
  showAccessDenied: false,
}

export default ProtectedRoute
